import {
  ensure,
  type Match,
  type Pair,
  type Scoring,
  type Tournament,
} from "./model";

export function scoringFor(t: Tournament, match: Match): Scoring {
  return (
    match.scoring ?? t.config.stageScoring[match.stage] ?? t.config.scoring
  );
}
export function setWinner(score: Pair, rule: Scoring): 0 | 1 | null {
  const [a, b] = score;
  ensure(
    [a, b].every((v) => Number.isInteger(v) && v >= 0 && v <= 199),
    "Scores must be whole numbers between 0 and 199.",
  );
  if (a === b) return null;
  const high = Math.max(a, b);
  const low = Math.min(a, b);
  if (rule.cap !== null && high > rule.cap) return null;
  const won =
    high >= rule.target &&
    (!rule.winByTwo || high - low >= 2 || high === rule.cap);
  return won ? (a > b ? 0 : 1) : null;
}
export function validateResult(sets: Pair[], rule: Scoring): 0 | 1 {
  ensure(
    sets.length >= rule.setsToWin && sets.length <= rule.setsToWin * 2 - 1,
    "Enter the required number of completed sets.",
  );
  const wins: Pair = [0, 0];
  for (const [index, set] of sets.entries()) {
    const winner = setWinner(set, rule);
    ensure(winner !== null, `Set ${index + 1} is not a valid completed set.`);
    const high = Math.max(...set);
    const low = Math.min(...set);
    // A final score cannot include rallies played after the set was already won.
    ensure(
      high === rule.target ||
        (rule.winByTwo && high - low === 2) ||
        (high === rule.cap && high - low === 1),
      `Set ${index + 1} continued after the winning point.`,
    );
    wins[winner]++;
    ensure(
      index === sets.length - 1 || Math.max(...wins) < rule.setsToWin,
      "The match ended before the last entered set.",
    );
  }
  ensure(
    Math.max(...wins) === rule.setsToWin,
    "Neither team has won the required sets.",
  );
  return wins[0] > wins[1] ? 0 : 1;
}
